import videojs from 'video.js'
import forEach from 'lodash/forEach.js'
import { CustomEvents } from '../types/index.ts'
import { revealElement, hideElement, registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

// Default options for the plugin.
const defaults = {
  language: 'en',
  title: null,
  description: null,
  url: null,
  embedCode: null,
  showLogo: true,
  showShare: true,
  showBackButton: false,
  backButtonLabel: null,
  theme: 'default',
  share: {
    email: {
      subject: null,
      body: null
    }
  }
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBOverlay extends Plugin {
  /**
   * Create a NFBOverlay plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())

    this.options = videojs.obj.merge(defaults, options)

    this.playerEl = this.player.el()
    this.controlPrefix = `${this.player.id()}-`
    this.els = {}
    this.wasForcePausedBy = null
    this.isVisible = true

    this.showOverlay = this.showOverlay.bind(this)
    this.hideOverlay = this.hideOverlay.bind(this)
    this.revealBlock = this.revealBlock.bind(this)
    this.hideBlock = this.hideBlock.bind(this)

    this.player.ready(() => {
      this.init()
    })
  }

  init () {
    this.log('Initializing NFB Overlay plugin')

    if (this.options.dynamicCssImport) {
      import('../sass/plugins/overlay.sass')
    }

    this.player.addClass('vjs-nfb-overlay')

    if (this.options.title || this.options.description || this.options.showShare || this.options.showBackButton) {
      this.createTopBarContainer()
    }

    if (this.options.title) {
      this.createTitleContainer()
    }

    if (this.options.showShare) {
      this.createShareIcon()
      this.createShareContainer()
    }

    if (this.options.showBackButton) {
      this.createBackButton()
    }

    if (this.options.description) {
      this.createInfoIcon()
      this.createInfoContainer()
    }

    if (this.options.showLogo) {
      this.createLogoContainer()
    }

    this.addListeners()
  }

  addListeners () {
    this.player.on('useractive', this.showOverlay)
    this.player.on('userinactive', this.hideOverlay)
    this.player.on('ended', this.showOverlay)
  }

  showOverlay () {
    if (!this.isVisible) {
      this.log('Show overlay')
      this.revealBlock(this.els.logoContainer, { slideLeft: true })
      this.revealBlock(this.els.topBarContainer, { slideDown: true })
      this.isVisible = true
    }
  }

  hideOverlay () {
    // if there is trigger warnings, player is paused but we still want to hide overlay.
    const nfbTriggerWarningsPlugin = this.player.nfbTriggerWarnings && this.player.nfbTriggerWarnings()
    if (!this.player.paused() || (nfbTriggerWarningsPlugin && nfbTriggerWarningsPlugin.isShowing)) {
      this.log('Hide overlay')
      this.hideBlock(this.els.logoContainer, { slideRight: true })
      this.hideBlock(this.els.topBarContainer, { slideUp: true })
      this.isVisible = false
    }
  }

  createTopBarContainer () {
    this.log('Creating top bar container')
    this.els.topBarContainer = this.playerEl.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.topBarContainer, 'top-bar-container')
    this.createControlsContainer()
  }

  createControlsContainer () {
    this.els.leftControlsContainer = this.els.topBarContainer.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.leftControlsContainer, 'left-controls-container')

    this.els.leftControlsFirstRowContainer = this.els.leftControlsContainer.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.leftControlsFirstRowContainer, 'left-controls-row')
    this.els.leftControlsSecondRowContainer = this.els.leftControlsContainer.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.leftControlsFirstRowContainer, 'left-controls-row')

    this.els.rightControlsContainer = this.els.topBarContainer.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.rightControlsContainer, 'right-controls-container')
  }

  createTitleContainer () {
    this.log('Creating title container')
    this.els.titleContainer = this.els.leftControlsFirstRowContainer.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.titleContainer, 'title-container')

    let title = this.options.title
    if (this.options.url) {
      title = `<a href="${this.options.url}" title="${this.options.title}" target="_blank">${this.options.title}</a>`
    }
    this.els.titleContainer.innerHTML = title
  }

  createInfoIcon () {
    this.log('Creating info icon')
    this.els.infoIcon = this.els.rightControlsContainer.appendChild(document.createElement('button'))
    this.els.infoIcon.setAttribute('title', this.player.localize('More information'))
    if (this.options.theme === 'balmoral-neue') {
      this.els.infoIcon.classList.add('nfb-button', 'nfb-button--round-with-icon', 'nfb-button--discreet', '_nfb-button--is-dark')
    }
    this.els.infoIcon.innerHTML = '<i class="nfb-icon icon-info-o"></i>'
  }

  createInfoContainer () {
    this.log('Creating info container')
    this.els.infoContainer = this.playerEl.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.infoContainer, 'info-container')
    this.hideBlock(this.els.infoContainer)
    this.els.infoContainer.innerHTML = `
      <button class="btn-close" title="${this.player.localize('Close')}"><i class="nfb-icon icon-delete-o"></i></button>
      <div class="content-container">
        <div class="auto-center">
          ${this.options.title ? `<h1>${this.options.title}</h1>`: ''}
          <div class="copy"></div>
        </div>
      </div>
    `

    // Unescape HTML
    const description = document.createElement('textarea')
    description.innerHTML = this.options.description
    const copyContainer = this.els.infoContainer.querySelector('.copy')
    copyContainer.innerHTML = description.value
    copyContainer.querySelectorAll('a').forEach(a => a.setAttribute('target', '_blank'))
    //

    this.els.closeInfoButton = this.els.infoContainer.querySelector('.btn-close')

    this.els.infoIcon.addEventListener('click', () => this.revealBlock(this.els.infoContainer, { forcePause: true, fade: true }))
    this.els.closeInfoButton.addEventListener('click', () => this.hideBlock(this.els.infoContainer, { fade: true }))
  }

  createShareIcon () {
    this.log('Creating share icon')
    this.els.shareIcon = this.els.rightControlsContainer.appendChild(document.createElement('button'))
    this.els.shareIcon.setAttribute('title', this.player.localize('Share'))
    if (this.options.theme === 'balmoral-neue') {
      this.els.shareIcon.classList.add('nfb-button', 'nfb-button--round-with-icon', 'nfb-button--discreet', '_nfb-button--is-dark')
    }
    this.els.shareIcon.innerHTML = '<i class="nfb-icon icon-share-o"></i>'
  }

  createShareContainer () {
    this.log('Creating share container')
    this.els.shareContainer = this.playerEl.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.shareContainer, 'share-container')
    this.hideBlock(this.els.shareContainer)
    this.els.shareContainer.innerHTML = `
      <button class="btn-close" title="${this.player.localize('Close')}"><i class="nfb-icon icon-delete-o"></i></button>
      <div class="content-container">
        <div class="auto-center">
          <div class="share-buttons">
            ${this.getFbShareButton()}
            ${this.getTwitterShareButton()}
          </div>
          ${this.getCopyLinkContainer()}
          ${this.options.embed && this.options.embed.html ? this.getEmbedContainer() : ''}
        </div>
      </div>
    `
    this.els.closeShareButton = this.els.shareContainer.querySelector('.btn-close')
    this.els.shareInput = this.els.shareContainer.querySelectorAll('input')

    const copyShareLinkButton = this.els.shareContainer.querySelector('[data-ui-el="copy-share-link-button"]')
    const copyEmbedButton = this.els.shareContainer.querySelector('[data-ui-el="copy-embed-button"]')

    const embedInput = this.els.shareContainer.querySelector('[data-ui-el="embed-input"]')
    const linkInput = this.els.shareContainer.querySelector('[data-ui-el="share-link-input"]')
    const shareLink = linkInput.dataset.uiDefaultText
    const embedDetail = embedInput.dataset.uiDefaultText

    this.els.shareIcon.addEventListener('click', () => {
      this.revealBlock(this.els.shareContainer, { forcePause: true, fade: true })
      if (shareLink) linkInput.value = shareLink
      if (embedDetail) embedInput.value = embedDetail
      linkInput.dataset.uiCopied = false.toString()
    })
    this.els.closeShareButton.addEventListener('click', () => this.hideBlock(this.els.shareContainer, { fade: true }))
    this.els.shareInput.forEach(input => {
      input.addEventListener('click', () => input.select())
    })

    this.copyInput(copyShareLinkButton, shareLink, linkInput)
    this.copyInput(copyEmbedButton, embedDetail, embedInput)

  }

  copyInput (copyButton, link, inputElement) {
    if (copyButton && link) {
      copyButton.addEventListener('click', async () => {
        await navigator.clipboard.writeText(link)
        const copiedText = inputElement.dataset.uiCopiedText
        inputElement.dataset.uiCopied = true.toString()
        if (copiedText) inputElement.value = copiedText
      })
    }
  }

  createBackButton () {
    this.log('Creating back button')
    this.els.backButton = document.createElement('button')
    this.els.backButton.setAttribute('aria-label', this.player.localize('Back to {1} page', [this.options.backButtonLabel ?? this.options.title]))
    this.els.backButton.addEventListener('click', this.dispatchBackButtonEvent)
    this.els.backButton.innerHTML = `<i class="nfb-icon icon-left-arrow"></i> ${this.options.backButtonLabel ?? ''}`
    this.els.leftControlsFirstRowContainer.prepend(this.els.backButton)
    this.assignControlAttributes(this.els.backButton, 'back-button')
  }

  dispatchBackButtonEvent (clickEvent) {
    clickEvent.target.dispatchEvent(new CustomEvent(CustomEvents.backButtonClicked, { composed: true, bubbles: true }))
  }

  getEmbedCode () {
    let html = this.options.embed.html
    if (this.options.embed.options) {
      forEach(this.options.embed.options, (val, name) => {
        html = html.replace(new RegExp(`\\(\\( *${name} *\\)\\)`, 'gi'), val)
      })
    }
    html = html.replace(/"/g, '&quot;')
    return html
  }

  getFbShareButton () {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURI(this.options.url)}`
    return `<a href="${url}" target="_blank" title="Facebook" class="nfb-button nfb-button--round-with-icon"><i class="nfb-icon icon-facebook"></i></a>`
  }

  getTwitterShareButton () {
    const url = `https://twitter.com/intent/tweet?text=${encodeURI(this.options.url)}`
    return `<a href="${url}" target="_blank" title="Twitter" class="nfb-button nfb-button--round-with-icon"><i class="nfb-icon icon-twitter-x"></i></a>`
  }

  getCopyLinkContainer () {
    return `
      <div class="nfb-player__input-container">
        <i class="nfb-icon icon-link"></i>
        <div class="nfb-form nfb-form__text-container--simple" data-input-is-active="false">
          <input
            class="nfb-font__body--lg nfb-form__text--simple"
            type="text"
            data-ui-el="share-link-input"
            data-ui-copied="false"
            readonly
            tabindex="-1"
            data-ui-default-text=${this.options.url}
            data-ui-copied-text="${this.player.localize('Link pasted to clipboard')}">
          </input>
        </div>
        <button class="nfb-button" data-ui-el="copy-share-link-button" aria-label="${this.player.localize('Copy share link')}">${this.player.localize('Copy')}</button>
      </div>`
  }

  getEmbedContainer () {
    return `
    <div class="nfb-player__input-container">
    <i class="nfb-icon icon-embed"></i>
    <div class="nfb-form nfb-form__text-container--simple" data-input-is-active="false">
      <input
        class="nfb-font__body--lg nfb-form__text--simple"
        type="text"
        data-ui-el="embed-input"
        data-ui-copied="false"
        readonly
        tabindex="-1"
        data-ui-default-text="${this.getEmbedCode()}"
        data-ui-copied-text="${this.player.localize('Link pasted to clipboard')}">
      </input>
    </div>
    <button class="nfb-button" data-ui-el="copy-embed-button" aria-label="${this.player.localize('Copy embed')}">${this.player.localize('Copy')}</button>
  </div>`

  }

  createLogoContainer () {
    this.log('Creating logo container')
    this.els.logoContainer = this.playerEl.appendChild(document.createElement('div'))
    this.assignControlAttributes(this.els.logoContainer, 'logo-container')
    this.els.logoContainer.innerHTML = `
      <a href="${this.options.url}" title="${this.player.localize('Go to NFB website')}" target="_blank">
        <i class="nfb-icon icon-nfb-onf-logo"></i>
      </a>
    `
  }

  revealBlock (container, options = {}) {
    if (container) {
      revealElement(container, options)
      if (options.forcePause && !this.player.paused()) {
        this.wasForcePausedBy = container
        this.player.pause()
      }
    }
  }

  hideBlock (container, options = {}) {
    if (container) {
      hideElement(container, options)
      if (this.wasForcePausedBy === container) {
        this.wasForcePausedBy = null
        this.player.play()
      }
    }
  }

  assignControlAttributes (element, controlName) {
    element.id = this.controlPrefix + controlName
    element.className = `${this.controlPrefix + controlName} ${controlName}`
  }
}

// Define default values for the plugin's `state` object here.
NFBOverlay.defaultState = {}

// Include the version number.
NFBOverlay.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbOverlay', NFBOverlay)

export default NFBOverlay
